import { ConnectedWallet } from '@privy-io/react-auth';
import {
  createNewIdentity,
  getIdentityModule,
  getWalletIdentityModuleAddress,
} from './contract-utils';
import { Hex, publicActions, zeroAddress } from 'viem';
import { privyWalletToClient } from '../account-utils';
import { TWalletClient } from './types';
import { EdKeypair } from '@ucans/ucans';
import { STORAGE_DID } from '../constants';
import * as ucans from '@ucans/ucans';
import { decryptPrivateKey, encryptPrivateKey } from './common';
import { getUserIdentity, modifyUserIdentity } from '@/db/queries';

class Identity {
  walletClient: TWalletClient | undefined;
  privateKey: Uint8Array | undefined;
  publicKey: Uint8Array | undefined;
  signingKey: EdKeypair | undefined;
  contractAddress: Hex | undefined;
  ucanToken: ucans.Ucan | undefined;
  salt: Uint8Array | undefined;

  getPublicKey() {
    if (!this.publicKey) throw new Error('Public key not initialized');
    return this.publicKey;
  }

  getPrivateKey() {
    if (!this.privateKey) throw new Error('Private key not initialized');
    return this.privateKey;
  }

  getIdentityAddress() {
    if (!this.contractAddress)
      throw new Error('Identity module address not initialized');
    return this.contractAddress;
  }

  async getAuthToken() {
    if (!this.signingKey || !this.contractAddress)
      throw new Error('Signing key or contract address not initialized');

    if (this.ucanToken && !ucans.isExpired(this.ucanToken))
      return ucans.encode(this.ucanToken);

    this.ucanToken = await ucans.build({
      audience: STORAGE_DID,
      issuer: this.signingKey,
      lifetimeInSeconds: 7 * 86400,
      capabilities: [
        {
          with: {
            scheme: 'storage',
            hierPart: this.contractAddress.toLowerCase(),
          },
          can: { namespace: 'file', segments: ['CREATE'] },
        },
      ],
    });

    return ucans.encode(this.ucanToken);
  }

  getWalletClient() {
    if (!this.walletClient) throw new Error('Wallet client not initialized');
    return this.walletClient;
  }

  async setWalletClient(wallet: ConnectedWallet) {
    if (!this.walletClient) {
      const baseWalletClient = await privyWalletToClient(wallet);
      this.walletClient = baseWalletClient.extend(publicActions);
    }
  }

  async getIdentityModuleAddress(wallet: ConnectedWallet) {
    await this.setWalletClient(wallet);

    const cachedModuleAddress = await getUserIdentity(wallet.address as Hex);

    if (
      cachedModuleAddress?.identityModuleAddress &&
      cachedModuleAddress.identityModuleAddress !== zeroAddress
    )
      return cachedModuleAddress.identityModuleAddress;

    const moduleAddress = await getWalletIdentityModuleAddress(wallet);

    if (moduleAddress !== zeroAddress)
      await modifyUserIdentity(wallet.address as Hex, {
        identityModuleAddress: moduleAddress,
      });

    return moduleAddress;
  }

  async initialize(moduleAddress: Hex) {
    if (moduleAddress === zeroAddress)
      return {
        initialized: false,
      };

    await this.setupExistingIdentityModule(moduleAddress);
    return {
      initialized: true,
    };
  }

  private async setupExistingIdentityModule(identityModuleAddress: Hex) {
    const walletClient = this.getWalletClient();

    const identity = await getIdentityModule(
      walletClient,
      identityModuleAddress
    );

    const { signingKey, publicKey, privateKey, salt } = identity;
    this.signingKey = signingKey;
    this.publicKey = publicKey;
    this.privateKey = privateKey;
    this.contractAddress = identityModuleAddress;

    this.salt = salt;
  }

  async setupNewIdentityModule() {
    const walletClient = this.getWalletClient();

    const { signingKey, publicKey, privateKey, identityModuleAddress, salt } =
      await createNewIdentity(walletClient);

    this.signingKey = signingKey;
    this.publicKey = publicKey;
    this.privateKey = privateKey;
    this.contractAddress = identityModuleAddress;
    this.salt = salt;
  }

  async decryptEnvelope(cipherText: string) {
    return await decryptPrivateKey(cipherText, this.getPrivateKey());
  }

  async encryptEnvelope(bytesToEncrypt: Uint8Array) {
    return await encryptPrivateKey(bytesToEncrypt, this.getPublicKey());
  }

  resetIdentity() {
    this.signingKey = undefined;
    this.publicKey = undefined;
    this.privateKey = undefined;
    this.contractAddress = undefined;
    this.salt = undefined;
    this.walletClient = undefined;
    this.ucanToken = undefined;
  }
}

export const IdentityInstance = new Identity();
